import * as React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useStyles } from './ApplePayButton.styles';
import { ApplePayType, AppleTokenObject, Confirmation3DS2, FastPayData, IWindowCustom, PaymentNetwork } from "types";
import { useStores } from "hooks";
import { formatAmount } from "utils";
import { paths } from "routing";
import { useHistory } from "react-router-dom";
import { TEXT } from "texts";

interface IApplePayButton {
  type: boolean | ApplePayType,
  onSend3DSData(data: Confirmation3DS2): void
}

declare const window: IWindowCustom;

const ApplePayButton: React.FC<IApplePayButton> = ({type, onSend3DSData}) => {
  const { formStore, appStore, errorStore } = useStores();
  const history = useHistory();
  const { i18n } = useTranslation();
  const classes = useStyles();

  const startSession = () => {
    if (!formStore.amount.isValid()) { return }

    if (type === 'setup') {
      setupSession()
    } else if (type === 'plain') {
      buySession()
    }
  }

  const setupSession = () => {
    if (window.ApplePaySession.openPaymentSetup) {
        window.ApplePaySession.openPaymentSetup(formStore.applePayMerchantId)
        .then(function (success: any) {
          if (success) {
            // Open payment setup successful
          } else {
            // Open payment setup failed
          }
        })
        .catch(function (e: Error) {
          console.log(e)
          // Open payment setup error handling
        });
    }
  }

  const buySession = () => {
    const paymentRequest = {
      "countryCode": "UA",
      "supportedCountries": [
        "AD",
        "AE",
        "AF",
        "AI",
        "AL",
        "UA",
        "AM"
      ],
      "currencyCode": 'UAH',
      "merchantCapabilities": [
        "supportsDebit",
        "supportsCredit",
        "supports3DS"
      ],
      "supportedNetworks": [
        "visa",
        "electron",
        "vPay",
        "masterCard",
        "maestro"
      ],
      "total": {
        "type": "final",
        "label": "",
        "amount": formatAmount({amount: formStore.amount.value, rounding: false, trim: true}),
      }
    }

    const applePaySession = new window.ApplePaySession(1, paymentRequest);

    applePaySession.onvalidatemerchant = (event: any) => {
      formStore.validateMerchant(event.validationURL)
        .then((response ) => {
          // confirm validation of payment session
          if (response.success) {
            applePaySession.completeMerchantValidation(response.opaqueApplePaySessionObject)
          }
        })
        .catch((error: Error) => {
          console.log(error)
          applePaySession.abort()
        })
    };

    applePaySession.onpaymentauthorized = (event: any) => {
      const token: AppleTokenObject = event.payment.token;

      const paymentResponseParams: FastPayData = {
        paymentMethod: {
          displayName: token.paymentMethod.displayName,
          network: token.paymentMethod.network,
          type: token.paymentMethod.type as PaymentNetwork,
          transactionIdentifier: token.transactionIdentifier
        },
        sourceApplePayPaymentData: btoa(JSON.stringify(token.paymentData))
      }
      formStore.togglePaymentCardVisible();
      formStore.clearCardValues();

      formStore.setFastPayData(paymentResponseParams);

      formStore.submit()
        .then((response) => {
          if (response.orderStatus) {
            const isResponseSuccess = response.orderStatus === 'CONFIRMED' || response.orderStatus === 'PROCESSED';

            isResponseSuccess
              ? applePaySession.completePayment(window.ApplePaySession.STATUS_SUCCESS)
              : applePaySession.completePayment(window.ApplePaySession.STATUS_FAILURE);

            history.push(isResponseSuccess ? paths.success : paths.error);
          }

          if (response.confirmationType === '3DS2' && response.confirmation3DS2) {
            appStore.setOrderId(response.orderId);
            onSend3DSData(response.confirmation3DS2);
          }
        })
        .catch(error => {
          console.log(error)
          errorStore.setError({ title: TEXT.error, description: (error as Error).message });
        })
    }

    applePaySession.begin();
  }

  return (
    <div className={classes.payBtnWrapper}>
      <button
        className={type as string}
        style={{ApplePayButtonType: type } as React.CSSProperties}
        onClick={startSession}
        lang={i18n.language === 'ua' ? 'uk-UA' : i18n.language}
      ></button>
    </div>
  );
};

export default observer(ApplePayButton);
